/**
 * Currency and dates.
 *
 * Mirrors api/app/i18n.py — the server needs these for emails and the
 * printed ticket, the browser needs them for every screen. Keep the two in
 * step; the tests in api/tests cover the Python side.
 *
 * Deliberately not `Intl.NumberFormat(locale)` for money. That would give a
 * Tamil member `₹` or a grouped format that is not Indonesian, and CLAUDE.md
 * is explicit: currency is always Indonesian, in all three locales.
 */
import type { Locale } from "../i18n";

const MONTHS: Record<Locale, readonly string[]> = {
  en: [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December",
  ],
  id: [
    "Januari", "Februari", "Maret", "April", "Mei", "Juni",
    "Juli", "Agustus", "September", "Oktober", "November", "Desember",
  ],
  ta: [
    "ஜனவரி", "பிப்ரவரி", "மார்ச்", "ஏப்ரல்", "மே", "ஜூன்",
    "ஜூலை", "ஆகஸ்ட்", "செப்டம்பர்", "அக்டோபர்", "நவம்பர்", "டிசம்பர்",
  ],
};

/**
 * The wall clock at the hall, and its label.
 *
 * Programme times are venue-local, never viewer-local. Doors open at a TIME,
 * not at an instant each reader converts for themselves — a member checking
 * their ticket from Chennai must see the same `19:00 WIB` the volunteer at
 * the door sees, or they turn up ninety minutes early and blame the app.
 *
 * Defaults are Jakarta, and `/api/v1/meta` overrides them at boot so the
 * browser and the server cannot disagree about what 19:00 means.
 */
let venueTimeZone = "Asia/Jakarta";
let venueTzLabel = "WIB";

export function setVenueZone(timeZone: string, label: string): void {
  if (timeZone) venueTimeZone = timeZone;
  if (label) venueTzLabel = label;
}

export const venueLabel = () => venueTzLabel;

interface Parts {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
}

/**
 * The calendar fields of an instant, as read at the venue.
 *
 * `formatToParts` rather than arithmetic on an offset: Indonesia has no
 * daylight saving today, but hardcoding +07:00 would quietly break if the
 * Sangam ever holds an event in a zone that does.
 */
function venueParts(value: Date): Parts {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: venueTimeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).formatToParts(value);

  const read = (type: string) => Number(parts.find((p) => p.type === type)?.value ?? 0);
  return {
    year: read("year"),
    month: read("month"),
    day: read("day"),
    // `en-GB` renders midnight as 24; normalise so 24:00 reads 00:00.
    hour: read("hour") % 24,
    minute: read("minute"),
  };
}

const toDate = (value: Date | string): Date =>
  typeof value === "string" ? new Date(value) : value;

/** `Rp155.000`. Dot thousands separator, no decimals, every locale. */
export function formatIdr(amount: number | null | undefined): string {
  if (amount === null || amount === undefined || Number.isNaN(amount)) return "—";
  const sign = amount < 0 ? "-" : "";
  const digits = Math.abs(Math.trunc(amount)).toString();
  return `${sign}Rp${digits.replace(/\B(?=(\d{3})+(?!\d))/g, ".")}`;
}

/**
 * `15 அக்டோபர் 2026` — month names localise, numerals never do, and the day
 * is the day AT THE VENUE. A 00:30 Jakarta programme is 17:30 the previous
 * day in UTC, and a viewer behind Jakarta would otherwise be shown the wrong
 * date entirely.
 */
export function formatDate(value: Date | string, locale: Locale): string {
  const { year, month, day } = venueParts(toDate(value));
  return `${day} ${MONTHS[locale][month - 1]} ${year}`;
}

/**
 * `19:00 WIB`. 24-hour, venue-local, labelled.
 *
 * The label is not translated, for the same reason `Rp` is not: it is what
 * is printed on the poster and said out loud at the hall.
 */
export function formatTime(value: Date | string, withLabel = true): string {
  const { hour, minute } = venueParts(toDate(value));
  const shown = `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
  return withLabel ? `${shown} ${venueTzLabel}` : shown;
}

export function formatDateTime(value: Date | string, locale: Locale): string {
  return `${formatDate(value, locale)}, ${formatTime(value)}`;
}

/**
 * Turn a `datetime-local` value into an instant, reading it as VENUE wall
 * clock rather than the browser's.
 *
 * A volunteer typing 19:00 into the programme form means seven in the evening
 * at the hall. `new Date("2026-11-08T19:00")` reads that as seven in the
 * evening wherever the laptop happens to be — identical in Jakarta, hours
 * wrong for anyone filing a programme from Chennai or on a machine whose
 * clock is set elsewhere. The whole app displays venue time; the form has to
 * accept it too, or the two disagree the moment someone travels.
 *
 * Solved by search rather than by adding a fixed offset: ask what wall clock
 * a guessed instant shows at the venue, correct by the difference, repeat. Two
 * passes settle it even across a daylight-saving boundary — Indonesia has
 * none, but `VENUE_TIMEZONE` is configurable and the next zone might.
 */
export function venueWallClockToISO(local: string): string {
  const [datePart, timePart = "00:00"] = local.split("T");
  const [y, mo, d] = datePart.split("-").map(Number);
  const [h, mi] = timePart.split(":").map(Number);
  const target = Date.UTC(y, mo - 1, d, h, mi);

  let guess = target;
  for (let i = 0; i < 2; i += 1) {
    const p = venueParts(new Date(guess));
    const asSeen = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute);
    guess += target - asSeen;
  }
  return new Date(guess).toISOString();
}

/** The inverse: an instant as a `datetime-local` value showing venue time,
 *  so an edit form opens on the number the volunteer originally typed. */
export function isoToVenueWallClock(iso: string): string {
  const p = venueParts(new Date(iso));
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${p.year}-${pad(p.month)}-${pad(p.day)}T${pad(p.hour)}:${pad(p.minute)}`;
}

/**
 * The seat-hold countdown, as `mm:ss`.
 *
 * Display only, and unaffected by any of the above: a duration has no zone.
 * Non-negotiable 6 — this reaching zero must never be what releases the
 * seat. It renders `held_until`, and the server is the authority even if
 * this tab was asleep.
 */
export function countdown(msRemaining: number): string {
  const total = Math.max(0, Math.floor(msRemaining / 1000));
  const mm = String(Math.floor(total / 60)).padStart(2, "0");
  const ss = String(total % 60).padStart(2, "0");
  return `${mm}:${ss}`;
}
